import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { ConsumerTable } from '@/components/inspect/ConsumerTable';
import { KeyValue } from '@/components/inspect/KeyValue';
import { MessagePeek } from '@/components/inspect/MessagePeek';
import { StreamCard } from '@/components/inspect/StreamCard';
import { TargetCard } from '@/components/inspect/TargetCard';
import { EmptyState, ErrorPage, LoadingPage, SectionTitle, Stat } from '@/components/primitives';
import { useHasRole } from '@/hooks/useHasRole';
import { useNatsOverview, useNatsStream } from '@/hooks/useSturmData';
import { bytes, count, text } from '@/lib/inspectFormat';

type Tab = 'subjects' | 'consumers' | 'messages';

export function NatsOverviewPage() {
  const { data, isLoading, isError, error } = useNatsOverview();
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('subjects');

  if (isLoading) {
    return <LoadingPage label="Reading JetStream…" />;
  }

  if (isError || !data) {
    return (
      <ErrorPage
        title="Could not load the NATS overview"
        message={error instanceof Error ? error.message : 'The API did not respond.'}
      />
    );
  }

  const streams = data.streams;
  const active = selected ?? streams[0]?.name ?? null;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">NATS overview</div>
          <div className="page-sub">
            Sturm does not use NATS — its realtime backplane is the Postgres outbox. This page
            observes the shared MedicalInsider broker read-only.
          </div>
        </div>
      </div>

      <TargetCard target={data.target} icon="stream">
        {data.server ? (
          <div className="kv" style={{ minWidth: 220 }}>
            <KeyValue k="Account memory" v={bytes(data.server.memory)} />
            <KeyValue k="Account storage" v={bytes(data.server.storage)} />
          </div>
        ) : null}
      </TargetCard>

      <div className="grid-4 mb-24">
        <Stat icon="layers" label="Streams" value={count(streams.length)} />
        <Stat
          icon="hash"
          label="Messages"
          value={count(streams.reduce((total, s) => total + s.messages, 0))}
        />
        <Stat
          icon="server"
          label="Stored"
          value={bytes(streams.reduce((total, s) => total + s.bytes, 0))}
        />
        <Stat
          icon="signal"
          label="Consumers"
          value={count(streams.reduce((total, s) => total + s.consumerCount, 0))}
        />
      </div>

      {streams.length === 0 ? (
        <div className="card">
          <EmptyState
            title={
              data.target.status === 'ok'
                ? 'No streams'
                : `${data.target.label} is ${data.target.status === 'unconfigured' ? 'not configured' : 'unreachable'}`
            }
            sub={text(data.target.error) === '—' ? 'The broker reports no JetStream streams.' : data.target.error!}
          />
        </div>
      ) : (
        <>
          <SectionTitle count={streams.length}>Streams</SectionTitle>
          <div className="grid-2 mb-24">
            {streams.map((stream) => (
              <StreamCard
                key={stream.name}
                stream={stream}
                selected={stream.name === active}
                onSelect={() => setSelected(stream.name)}
              />
            ))}
          </div>

          {active ? <StreamDetail stream={active} tab={tab} onTab={setTab} /> : null}
        </>
      )}
    </div>
  );
}

function StreamDetail({
  stream,
  tab,
  onTab,
}: {
  stream: string;
  tab: Tab;
  onTab: (tab: Tab) => void;
}) {
  const { data, isLoading, isError, error } = useNatsStream(stream);
  const isAdmin = useHasRole('admin');

  if (isLoading) {
    return <LoadingPage label={`Reading ${stream}…`} />;
  }

  if (isError || !data) {
    return (
      <ErrorPage
        title={`Could not read ${stream}`}
        message={error instanceof Error ? error.message : 'The broker did not respond.'}
      />
    );
  }

  return (
    <>
      <SectionTitle>{stream}</SectionTitle>

      <div className="tabs">
        <div className={`tab ${tab === 'subjects' ? 'active' : ''}`} onClick={() => onTab('subjects')}>
          <Icon name="hash" size={14} /> Subjects{' '}
          <span className="count">{data.subjectCounts.length}</span>
        </div>
        <div className={`tab ${tab === 'consumers' ? 'active' : ''}`} onClick={() => onTab('consumers')}>
          <Icon name="signal" size={14} /> Consumers{' '}
          <span className="count">{data.consumers.length}</span>
        </div>
        <div className={`tab ${tab === 'messages' ? 'active' : ''}`} onClick={() => onTab('messages')}>
          <Icon name="eye" size={14} /> Messages
        </div>
      </div>

      {tab === 'subjects' ? (
        <div className="card">
          {data.subjectCounts.length === 0 ? (
            <EmptyState
              title="No subject counts"
              sub="The stream holds no messages, so the server reports no per-subject state."
            />
          ) : (
            <>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Messages</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subjectCounts.map((entry) => (
                    <tr key={entry.subject}>
                      <td className="mono">{entry.subject}</td>
                      <td className="tbl-num">{count(entry.messages)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.subjectCountsTruncated ? (
                <div className="card-pad tiny muted">
                  The server capped the subject list; {count(data.stream.numSubjects)} subjects exist
                  in total.
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {tab === 'consumers' ? (
        <div className="card">
          <ConsumerTable consumers={data.consumers} stream={data.stream} />
        </div>
      ) : null}

      {tab === 'messages' ? (
        isAdmin ? (
          <MessagePeek stream={stream} />
        ) : (
          <div className="card">
            <EmptyState
              title="Message bodies require the Admin role"
              sub="Subjects on the shared broker can carry personal data, so reading a body is held to a higher floor than the counters above."
            />
          </div>
        )
      ) : null}
    </>
  );
}
